<template>
    <v-container fluid class="fill-height align-start pa-0">
        <v-col cols="12" class="d-flex flex-column fill-height">
            <v-tabs v-model="tab" color="primary" density="compact">
                <v-tab value="filters">Filters</v-tab>
                <v-tab value="boss">Boss Configuration</v-tab>
                <v-tab value="status">Status Configuration</v-tab>
                <v-tab value="statusGroups">Status Groups</v-tab>
                <v-tab value="class">Skill Class Configuration</v-tab>
                <v-tab value="skillAmp">Skill Amplification</v-tab>
            </v-tabs>
            <v-divider></v-divider>

            <v-tabs-window v-model="tab" class="flex-grow-1 overflow-hidden" style="height: 100%;">
                <!-- Filters Tab -->
                <v-tabs-window-item value="filters" class="fill-height">
                    <v-container fluid class="fill-height align-start">
                        <v-row class="fill-height">
                            <v-col cols="12" md="6" class="d-flex flex-column">
                                <div class="text-h6 mb-2">Analysis Filters</div>
                                <div class="text-caption text-grey mb-4">Configure which entities appear in the Analysis and By Skill target dropdowns.</div>

                                <v-text-field v-model.number="localFilters.minEntityDamage" label="Minimum Total Damage"
                                    type="number" variant="outlined" density="compact" hide-details class="mb-4"
                                    hint="Entities with less total damage taken will be hidden" persistent-hint
                                    @update:model-value="triggerSaveFilters">
                                    <template v-slot:append-inner>
                                        <span class="text-caption text-grey">{{ formatNumber(localFilters.minEntityDamage) }}</span>
                                    </template>
                                </v-text-field>

                                <v-text-field v-model.number="localFilters.minDebuffCount" label="Minimum Tracked Debuff Count"
                                    type="number" variant="outlined" density="compact" hide-details class="mb-4"
                                    hint="Entities with fewer tracked debuffs recorded will be hidden (uses Status Configuration list)" persistent-hint
                                    @update:model-value="triggerSaveFilters">
                                </v-text-field>

                                <v-fade-transition>
                                    <span v-if="savingFilters" class="text-caption text-grey">Saving...</span>
                                </v-fade-transition>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-tabs-window-item>

                <!-- Boss Configuration Tab -->
                <v-tabs-window-item value="boss" class="fill-height">
                    <v-container fluid class="fill-height align-start">
                        <v-row class="fill-height">
                            <v-col cols="12" class="d-flex flex-column fill-height">
                                <div class="d-flex align-center mb-4 flex-shrink-0">
                                    <div>
                                        <div class="text-h6">Boss Configuration</div>
                                        <div class="text-caption text-grey">Search and add boss race IDs. Bosses will be marked with ⭐ in the Analysis and By Skill dropdowns.</div>
                                    </div>
                                    <v-spacer></v-spacer>
                                    <v-fade-transition>
                                        <span v-if="savingBoss" class="text-caption text-grey mr-2">Saving...</span>
                                    </v-fade-transition>
                                    <v-btn color="warning" variant="outlined" prepend-icon="mdi-database-refresh"
                                        size="small" :loading="rebuildingLeaderboard"
                                        @click="confirmRebuildLeaderboard">Rebuild Leaderboard</v-btn>
                                </div>

                                <v-alert v-if="rebuildStatusMessage" :type="rebuildStatusIsError ? 'error' : 'success'"
                                    density="compact" variant="tonal" closable class="mb-4"
                                    @click:close="rebuildStatusMessage = ''">
                                    {{ rebuildStatusMessage }}
                                </v-alert>

                                <!-- Search / Add Area -->
                                <div class="mb-4">
                                    <v-autocomplete v-model="selectedBossToAdd" :items="allRaces" item-title="name"
                                        item-value="id" label="Search for Race (Name or ID)"
                                        placeholder="Type to search..." prepend-inner-icon="mdi-magnify"
                                        variant="outlined" density="comfortable" hide-details clearable
                                        auto-select-first return-object @update:model-value="onAddBoss">
                                        <template v-slot:item="{ props, item }">
                                            <v-list-item v-bind="props" :title="item.raw.name"
                                                :subtitle="`Race ID: ${item.raw.id}`">
                                            </v-list-item>
                                        </template>
                                    </v-autocomplete>
                                </div>

                                <!-- Tracked boss list -->
                                <v-card variant="outlined" class="flex-grow-1 overflow-y-auto" color="surface">
                                    <v-card-title class="text-subtitle-2 bg-grey-darken-4 py-2">
                                        Tracked Bosses ({{ localBosses.length }})
                                        <span v-if="localBosses.length === 0"
                                            class="float-right text-caption text-grey font-weight-normal">
                                            No bosses configured
                                        </span>
                                    </v-card-title>
                                    <v-divider></v-divider>
                                    <v-card-text class="pa-2">
                                        <div v-if="localBosses.length === 0" class="text-center text-grey py-8">
                                            <v-icon size="48" color="grey-darken-2"
                                                class="mb-2">mdi-skull-crossbones</v-icon>
                                            <div>No bosses configured.</div>
                                            <div class="text-caption">Use the search bar above to add specific boss races.</div>
                                        </div>

                                        <v-table density="compact" v-else>
                                            <thead>
                                                <tr>
                                                    <th class="text-left">Race Name</th>
                                                    <th class="text-left" style="width: 100px">Race ID</th>
                                                    <th class="text-left" style="width: 160px">Expected HP</th>
                                                    <th class="text-right" style="width: 80px">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="id in localBosses" :key="id">
                                                    <td>{{ getRaceName(id) }}</td>
                                                    <td class="text-grey">{{ id }}</td>
                                                    <td>
                                                        <v-text-field v-model.number="localBossExpectedHP[id]"
                                                            type="number" density="compact" variant="outlined"
                                                            hide-details placeholder="Auto-detected" single-line
                                                            @update:model-value="triggerSaveBossExpectedHP">
                                                        </v-text-field>
                                                    </td>
                                                    <td class="text-right">
                                                        <v-btn icon="mdi-close" size="x-small" variant="text"
                                                            color="grey" @click="removeBoss(id)"></v-btn>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </v-table>
                                        <div v-if="localBosses.length > 0" class="text-caption text-grey pa-2">
                                            Expected HP verifies a raid actually completed (party's total damage
                                            against the boss must clear it) - a disappear packet alone can be a false
                                            positive (despawn/reset). This fills in automatically the first time a real
                                            kill reports the boss's HP, and only ever adjusts upward from there - you
                                            can also enter it manually (e.g. ahead of the first kill). Leave blank to
                                            only require the disappear packet until one is detected.
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-tabs-window-item>

                <!-- Status Configuration Tab -->
                <v-tabs-window-item value="status" class="fill-height">
                    <v-container fluid class="fill-height align-start">
                        <v-row class="fill-height">
                            <v-col cols="12" class="d-flex flex-column fill-height">
                                <div class="d-flex align-center mb-4 flex-shrink-0">
                                    <div>
                                        <div class="text-h6">Analysis Configuration</div>
                                        <div class="text-caption text-grey">Search and add statuses to track in Analysis
                                            coverage
                                            statistics.</div>
                                    </div>
                                    <v-spacer></v-spacer>
                                    <v-fade-transition>
                                        <span v-if="savingStatus" class="text-caption text-grey mr-2">Saving...</span>
                                    </v-fade-transition>
                                </div>

                                <!-- Search / Add Area -->
                                <div class="mb-4">
                                    <v-autocomplete v-model="selectedStatusToAdd" :items="allStatuses" item-title="name"
                                        item-value="id" label="Search for Status (Name or ID)"
                                        placeholder="Type to search..." prepend-inner-icon="mdi-magnify"
                                        variant="outlined" density="comfortable" hide-details clearable
                                        auto-select-first return-object @update:model-value="onAddStatus">
                                        <template v-slot:item="{ props, item }">
                                            <v-list-item v-bind="props" :title="item.raw.name"
                                                :subtitle="`ID: ${item.raw.id}`">
                                                <template v-slot:prepend>
                                                    <v-avatar size="24" rounded="0" class="mr-2">
                                                        <v-img :src="getStatusIconUrl(item.raw.id)" cover
                                                            @error="(e: any) => console.error('[ImgError] Status (Search):', item.raw.id, getStatusIconUrl(item.raw.id), e)">
                                                            <template v-slot:error><v-icon
                                                                    icon="mdi-help-circle-outline"></v-icon></template>
                                                        </v-img>
                                                    </v-avatar>
                                                </template>
                                            </v-list-item>
                                        </template>
                                    </v-autocomplete>
                                </div>

                                <!-- tracked list -->
                                <v-card variant="outlined" class="flex-grow-1 overflow-y-auto" color="surface">
                                    <v-card-title class="text-subtitle-2 bg-grey-darken-4 py-2">
                                        Tracked Statuses ({{ localTracked.length }})
                                        <span v-if="localTracked.length === 0"
                                            class="float-right text-caption text-grey font-weight-normal">
                                            All statuses shown by default
                                        </span>
                                    </v-card-title>
                                    <v-divider></v-divider>
                                    <v-card-text class="pa-2">
                                        <div v-if="localTracked.length === 0" class="text-center text-grey py-8">
                                            <v-icon size="48" color="grey-darken-2"
                                                class="mb-2">mdi-playlist-plus</v-icon>
                                            <div>No specific filters active. All statuses will be shown.</div>
                                            <div class="text-caption">Use the search bar above to add specific statuses
                                                to track.</div>
                                        </div>

                                        <v-table density="compact" v-else>
                                            <thead>
                                                <tr>
                                                    <th class="text-left" style="width: 50px">Icon</th>
                                                    <th class="text-left">Status Name</th>
                                                    <th class="text-left" style="width: 100px">ID</th>
                                                    <th class="text-right" style="width: 80px">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="id in localTracked" :key="id">
                                                    <td>
                                                        <v-avatar size="24" rounded="0">
                                                            <v-img :src="getStatusIconUrl(id)" cover
                                                                @error="(e: any) => console.error('[ImgError] Status (Table):', id, getStatusIconUrl(id), e)">
                                                                <template v-slot:error><v-icon size="small"
                                                                        icon="mdi-help-circle-outline"></v-icon></template>
                                                            </v-img>
                                                        </v-avatar>
                                                    </td>
                                                    <td>{{ getStatusName(id) }}</td>
                                                    <td class="text-grey">{{ id }}</td>
                                                    <td class="text-right">
                                                        <v-btn icon="mdi-close" size="x-small" variant="text"
                                                            color="grey" @click="removeStatus(id)"></v-btn>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </v-table>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-tabs-window-item>

                <!-- Skill Class Configuration Tab -->
                <v-tabs-window-item value="class" class="fill-height">
                    <v-container fluid class="fill-height align-start">
                        <v-row class="fill-height">
                            <v-col cols="12" class="d-flex flex-column fill-height">
                                <div class="d-flex align-center mb-4 flex-shrink-0">
                                    <div>
                                        <div class="text-h6">Class Configuration</div>
                                        <div class="text-caption text-grey">Define classes by grouping skills. A player
                                            using any skill in a group will be classified as that class.</div>
                                    </div>
                                    <v-spacer></v-spacer>
                                    <v-fade-transition>
                                        <span v-if="savingClass" class="text-caption text-grey mr-2">Saving...</span>
                                    </v-fade-transition>
                                    <v-btn color="primary" prepend-icon="mdi-plus" @click="createNewClass"
                                        size="small">Add
                                        Class</v-btn>
                                </div>

                                <v-card variant="outlined" class="flex-grow-1 overflow-y-auto" color="surface">
                                    <v-card-text class="pa-4">
                                        <div v-if="localClasses.length === 0" class="text-center text-grey py-8">
                                            <v-icon size="48" color="grey-darken-2"
                                                class="mb-2">mdi-account-group</v-icon>
                                            <div>No classes defined.</div>
                                            <div class="text-caption">Click "Add Class" to create a new skill group.
                                            </div>
                                        </div>

                                        <v-expansion-panels v-else variant="accordion" multiple>
                                            <v-expansion-panel v-for="(cls, index) in localClasses" :key="index">
                                                <v-expansion-panel-title>
                                                    <span class="font-weight-bold mr-2">{{ cls.name || 'Unnamed Class'
                                                        }}</span>
                                                    <span class="text-caption text-grey">({{ cls.skills.length }}
                                                        skills)</span>
                                                </v-expansion-panel-title>
                                                <v-expansion-panel-text>
                                                    <v-row dense class="align-center mb-2">
                                                        <v-col cols="12" sm="6">
                                                            <v-text-field v-model="cls.name" label="Class Name"
                                                                density="compact" variant="outlined" hide-details
                                                                @update:model-value="triggerSaveClass"></v-text-field>
                                                        </v-col>
                                                        <v-col cols="12" sm="6" class="text-right">
                                                            <v-btn color="error" variant="text" size="small"
                                                                prepend-icon="mdi-delete"
                                                                @click="deleteClass(index)">Delete Class</v-btn>
                                                        </v-col>
                                                    </v-row>

                                                    <v-divider class="mb-3"></v-divider>

                                                    <div class="mb-3">
                                                        <v-autocomplete :items="allSkills" item-title="name"
                                                            item-value="id" label="Add Skill to Class"
                                                            placeholder="Search skill..."
                                                            prepend-inner-icon="mdi-magnify" variant="outlined"
                                                            density="compact" hide-details clearable auto-select-first
                                                            return-object
                                                            @update:model-value="(item) => addSkillToClass(index, item)">
                                                            <template v-slot:item="{ props, item }">
                                                                <v-list-item v-bind="props" :title="item.raw.name"
                                                                    :subtitle="`ID: ${item.raw.id}`">
                                                                    <template v-slot:prepend>
                                                                        <v-avatar size="24" rounded="0" class="mr-2">
                                                                            <v-img
                                                                                :src="getSkillIconUrl(item.raw.id, region)"
                                                                                cover>
                                                                                <template v-slot:error><v-icon
                                                                                        icon="mdi-help-circle-outline"></v-icon></template>
                                                                            </v-img>
                                                                        </v-avatar>
                                                                    </template>
                                                                </v-list-item>
                                                            </template>
                                                        </v-autocomplete>
                                                    </div>

                                                    <v-chip-group column>
                                                        <v-chip v-for="skillId in cls.skills" :key="skillId" closable
                                                            @click:close="removeSkillFromClass(index, skillId)">
                                                            <v-avatar start rounded="0">
                                                                <v-img :src="getSkillIconUrl(skillId, region)" cover>
                                                                    <template v-slot:error><v-icon
                                                                            icon="mdi-help-circle-outline"></v-icon></template>
                                                                </v-img>
                                                            </v-avatar>
                                                            {{ getSkillName(skillId) }}
                                                        </v-chip>
                                                    </v-chip-group>
                                                </v-expansion-panel-text>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-tabs-window-item>

                <!-- Status Groups Tab -->
                <v-tabs-window-item value="statusGroups" class="fill-height">
                    <v-container fluid class="fill-height align-start">
                        <v-row class="fill-height">
                            <v-col cols="12" class="d-flex flex-column fill-height">
                                <div class="d-flex align-center mb-4 flex-shrink-0">
                                    <div>
                                        <div class="text-h6">Status Groups</div>
                                        <div class="text-caption text-grey">Group similar statuses together. Grouped
                                            statuses will combine their coverage in analysis.</div>
                                    </div>
                                    <v-spacer></v-spacer>
                                    <v-fade-transition>
                                        <span v-if="savingGroups" class="text-caption text-grey mr-2">Saving...</span>
                                    </v-fade-transition>
                                    <v-btn color="primary" prepend-icon="mdi-plus" @click="createNewGroup"
                                        size="small">Add
                                        Group</v-btn>
                                </div>

                                <v-card variant="outlined" class="flex-grow-1 overflow-y-auto" color="surface">
                                    <v-card-text class="pa-4">
                                        <div v-if="localGroups.length === 0" class="text-center text-grey py-8">
                                            <v-icon size="48" color="grey-darken-2"
                                                class="mb-2">mdi-group</v-icon>
                                            <div>No status groups defined.</div>
                                            <div class="text-caption">Click "Add Group" to create a new status group.
                                            </div>
                                        </div>

                                        <v-expansion-panels v-else variant="accordion" multiple>
                                            <v-expansion-panel v-for="(group, index) in localGroups" :key="index">
                                                <v-expansion-panel-title>
                                                    <span class="font-weight-bold mr-2">{{ group.name || 'Unnamed Group'
                                                        }}</span>
                                                    <span class="text-caption text-grey">({{ group.statuses.length }}
                                                        statuses)</span>
                                                </v-expansion-panel-title>
                                                <v-expansion-panel-text>
                                                    <v-row dense class="align-center mb-2">
                                                        <v-col cols="12" sm="6">
                                                            <v-text-field v-model="group.name" label="Group Name"
                                                                density="compact" variant="outlined" hide-details
                                                                @update:model-value="triggerSaveGroups"></v-text-field>
                                                        </v-col>
                                                        <v-col cols="12" sm="6" class="text-right">
                                                            <v-btn color="error" variant="text" size="small"
                                                                prepend-icon="mdi-delete"
                                                                @click="deleteGroup(index)">Delete Group</v-btn>
                                                        </v-col>
                                                    </v-row>

                                                    <v-divider class="mb-3"></v-divider>

                                                    <div class="mb-3">
                                                        <v-autocomplete :items="allStatuses" item-title="name"
                                                            item-value="id" label="Add Status to Group"
                                                            placeholder="Search status..."
                                                            prepend-inner-icon="mdi-magnify" variant="outlined"
                                                            density="compact" hide-details clearable auto-select-first
                                                            return-object
                                                            @update:model-value="(item) => addStatusToGroup(index, item)">
                                                            <template v-slot:item="{ props, item }">
                                                                <v-list-item v-bind="props" :title="item.raw.name"
                                                                    :subtitle="`ID: ${item.raw.id}`">
                                                                    <template v-slot:prepend>
                                                                        <v-avatar size="24" rounded="0" class="mr-2">
                                                                            <v-img
                                                                                :src="getStatusIconUrl(item.raw.id)"
                                                                                cover>
                                                                                <template v-slot:error><v-icon
                                                                                        icon="mdi-help-circle-outline"></v-icon></template>
                                                                            </v-img>
                                                                        </v-avatar>
                                                                    </template>
                                                                </v-list-item>
                                                            </template>
                                                        </v-autocomplete>
                                                    </div>

                                                    <v-chip-group column>
                                                        <v-chip v-for="statusId in group.statuses" :key="statusId" closable
                                                            @click:close="removeStatusFromGroup(index, statusId)">
                                                            <v-avatar start rounded="0">
                                                                <v-img :src="getStatusIconUrl(statusId)" cover>
                                                                    <template v-slot:error><v-icon
                                                                            icon="mdi-help-circle-outline"></v-icon></template>
                                                                </v-img>
                                                            </v-avatar>
                                                            {{ getStatusName(statusId) }}
                                                        </v-chip>
                                                    </v-chip-group>
                                                </v-expansion-panel-text>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-tabs-window-item>

                <!-- Skill Amplification Tab -->
                <v-tabs-window-item value="skillAmp" class="fill-height">
                    <skill-amplification-config />
                </v-tabs-window-item>
            </v-tabs-window>
        </v-col>
    </v-container>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch, inject } from "vue";
import { condNameMap, trackedStatuses, skillNameMap, skillClasses, SkillClass, region, db, statusGroups, analysisFilters, AnalysisFilters, trackedBosses, raceNameMap } from "@/store";
import { saveConfig, loadConfig } from "@/apicall";
import { getSkillIconUrl, getStatusIconUrl } from "@/util";
import SkillAmplificationConfig from "./SkillAmplificationConfig.vue";


export default defineComponent({
    name: "Configuration",
    components: { SkillAmplificationConfig },
    setup() {
        const tab = ref("filters");

        // --- Filters Config Logic ---
        const localFilters = ref<AnalysisFilters>({ ...analysisFilters.value });
        const savingFilters = ref(false);

        const formatNumber = (n: number) => {
            if (!n && n !== 0) return '';
            return n.toLocaleString();
        };

        let debounceFiltersTimer: ReturnType<typeof setTimeout> | null = null;
        const triggerSaveFilters = () => {
            analysisFilters.value = { ...localFilters.value };
            savingFilters.value = true;
            if (debounceFiltersTimer) clearTimeout(debounceFiltersTimer);
            debounceFiltersTimer = setTimeout(async () => {
                try {
                    await saveConfig("analysis_filters", localFilters.value);
                } catch (e) {
                    console.error("Auto-save filters failed", e);
                } finally {
                    savingFilters.value = false;
                }
            }, 1000);
        };

        // --- Boss Config Logic ---
        const localBosses = ref<number[]>([]);
        const savingBoss = ref(false);
        const selectedBossToAdd = ref<any>(null);

        const allRaces = computed(() => {
            return Object.entries(raceNameMap.value).map(([id, name]) => ({
                id: Number(id),
                name: `${name} (${id})`,
                rawName: String(name)
            })).sort((a, b) => a.rawName.localeCompare(b.rawName));
        });

        const getRaceName = (id: number) => {
            return raceNameMap.value[id] || `Unknown (${id})`;
        };

        const onAddBoss = (item: any) => {
            if (!item) return;
            const id = item.id;
            if (!localBosses.value.includes(id)) {
                localBosses.value.push(id);
                triggerSaveBoss();
            }
            setTimeout(() => {
                selectedBossToAdd.value = null;
            }, 0);
        };

        const removeBoss = (id: number) => {
            localBosses.value = localBosses.value.filter(x => x !== id);
            triggerSaveBoss();
        };

        let debounceBossTimer: ReturnType<typeof setTimeout> | null = null;
        const triggerSaveBoss = () => {
            trackedBosses.value = [...localBosses.value];
            savingBoss.value = true;
            if (debounceBossTimer) clearTimeout(debounceBossTimer);
            debounceBossTimer = setTimeout(async () => {
                try {
                    await saveConfig("tracked_bosses", localBosses.value);
                } catch (e) {
                    console.error("Auto-save boss config failed", e);
                } finally {
                    savingBoss.value = false;
                }
            }, 1000);
        };

        // --- Boss Expected HP (completion-verification threshold) ---
        // Keyed by race id (as a string, since JSON object keys are always
        // strings) - see getBossExpectedHP in backfill.go/combat_state.go.
        const localBossExpectedHP = ref<Record<number, number>>({});

        let debounceBossHPTimer: ReturnType<typeof setTimeout> | null = null;
        const triggerSaveBossExpectedHP = () => {
            savingBoss.value = true;
            if (debounceBossHPTimer) clearTimeout(debounceBossHPTimer);
            debounceBossHPTimer = setTimeout(async () => {
                try {
                    // Only persist positive values - blank/0/invalid entries
                    // just mean "not configured" (disappear-alone fallback).
                    const toSave: Record<string, number> = {};
                    for (const [id, hp] of Object.entries(localBossExpectedHP.value)) {
                        if (hp > 0) toSave[id] = hp;
                    }
                    await saveConfig("boss_expected_hp", toSave);
                } catch (e) {
                    console.error("Auto-save boss expected HP failed", e);
                } finally {
                    savingBoss.value = false;
                }
            }, 1000);
        };

        // --- Leaderboard Rebuild ---
        // Force-recomputes fights/fight_participants from the raw event logs
        // using whatever the current fight-finalize math is - for recovering
        // when a bug in that computation (e.g. a DPS formula fix) already
        // wrote wrong numbers into rows the normal startup backfill pass
        // would never revisit (see rebuildAllFights in backfill.go).
        const rebuildingLeaderboard = ref(false);
        const rebuildStatusMessage = ref("");
        const rebuildStatusIsError = ref(false);

        const confirmRebuildLeaderboard = async () => {
            if (!confirm(
                "Rebuild the leaderboard from historical session logs? This recomputes every past fight's stats from scratch using the current calculation - useful after a DPS formula fix. It runs in the background and may take a while for large logs. The currently-active session is skipped until the app restarts."
            )) {
                return;
            }
            rebuildingLeaderboard.value = true;
            rebuildStatusMessage.value = "";
            try {
                const resp = await fetch("/api/leaderboard/rebuild", { method: "POST" });
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok) {
                    throw new Error(data?.message || `Request failed: ${resp.status}`);
                }
                rebuildStatusIsError.value = false;
                rebuildStatusMessage.value = data.message || "Leaderboard rebuild started.";
            } catch (e: any) {
                rebuildStatusIsError.value = true;
                rebuildStatusMessage.value = e?.message || "Failed to start leaderboard rebuild.";
            } finally {
                rebuildingLeaderboard.value = false;
            }
        };

        // --- Status Config Logic ---
        const localTracked = ref<number[]>([]);
        const savingStatus = ref(false);
        const selectedStatusToAdd = ref<any>(null);

        const allStatuses = computed(() => {
            return Object.entries(condNameMap.value).map(([id, name]) => ({
                id: Number(id),
                name: `${name} (${id})`,
                rawName: name
            })).sort((a, b) => a.rawName.localeCompare(b.rawName));
        });

        const getStatusName = (id: number) => {
            return condNameMap.value[id] || `Unknown (${id})`;
        };

        const onAddStatus = (item: any) => {
            if (!item) return;
            const id = item.id;
            if (!localTracked.value.includes(id)) {
                localTracked.value.push(id);
                triggerSaveStatus();
            }
            setTimeout(() => {
                selectedStatusToAdd.value = null;
            }, 0);
        };

        const removeStatus = (id: number) => {
            localTracked.value = localTracked.value.filter(x => x !== id);
            triggerSaveStatus();
        };

        let debounceStatusTimer: ReturnType<typeof setTimeout> | null = null;
        const triggerSaveStatus = () => {
            trackedStatuses.value = [...localTracked.value];
            savingStatus.value = true;
            if (debounceStatusTimer) clearTimeout(debounceStatusTimer);
            debounceStatusTimer = setTimeout(async () => {
                try {
                    await saveConfig("tracked_statuses", localTracked.value);
                } catch (e) {
                    console.error("Auto-save status failed", e);
                } finally {
                    savingStatus.value = false;
                }
            }, 1000);
        };

        // --- Skill Class Config Logic ---
        const localClasses = ref<SkillClass[]>([]);
        const savingClass = ref(false);

        const allSkills = computed(() => {
            return Object.entries(skillNameMap.value).map(([id, name]) => ({
                id: Number(id),
                name: `${name} (${id})`,
                rawName: name
            })).sort((a, b) => a.rawName.localeCompare(b.rawName));
        });

        const getSkillName = (id: number) => {
            return skillNameMap.value[id] || `Unknown (${id})`;
        };

        const createNewClass = () => {
            localClasses.value.push({
                name: "New Class",
                skills: []
            });
            triggerSaveClass();
        };

        const deleteClass = (index: number) => {
            if (confirm("Are you sure you want to delete this class?")) {
                localClasses.value.splice(index, 1);
                triggerSaveClass();
            }
        };

        const addSkillToClass = (classIndex: number, item: any) => {
            if (!item) return;
            const cls = localClasses.value[classIndex];
            if (!cls.skills.includes(item.id)) {
                cls.skills.push(item.id);
                triggerSaveClass();
            }
        };

        const removeSkillFromClass = (classIndex: number, skillId: number) => {
            const cls = localClasses.value[classIndex];
            cls.skills = cls.skills.filter(id => id !== skillId);
            triggerSaveClass();
        };

        let debounceClassTimer: ReturnType<typeof setTimeout> | null = null;
        const triggerSaveClass = () => {
            // Update store
            skillClasses.value = JSON.parse(JSON.stringify(localClasses.value)); // Deep copy to trigger reactivity safely
            savingClass.value = true;
            if (debounceClassTimer) clearTimeout(debounceClassTimer);
            debounceClassTimer = setTimeout(async () => {
                try {
                    await saveConfig("skill_classes", localClasses.value);
                } catch (e) {
                    console.error("Auto-save class failed", e);
                } finally {
                    savingClass.value = false;
                }
            }, 1000);
        };

        // --- Status Groups Logic ---
        const localGroups = ref<any[]>([]);
        const savingGroups = ref(false);

        const createNewGroup = () => {
            localGroups.value.push({
                name: "New Group",
                statuses: []
            });
            triggerSaveGroups();
        };

        const deleteGroup = (index: number) => {
            if (confirm("Are you sure you want to delete this status group?")) {
                localGroups.value.splice(index,1);
                triggerSaveGroups();
            }
        };

        const addStatusToGroup = (groupIndex: number, item: any) => {
            if (!item) return;
            const group = localGroups.value[groupIndex];
            if (!group.statuses.includes(item.id)) {
                group.statuses.push(item.id);
                triggerSaveGroups();
            }
        };

        const removeStatusFromGroup = (groupIndex: number, statusId: number) => {
            const group = localGroups.value[groupIndex];
            group.statuses = group.statuses.filter((id: number) => id !== statusId);
            triggerSaveGroups();
        };

        let debounceGroupsTimer: ReturnType<typeof setTimeout> | null = null;
        const triggerSaveGroups = () => {
            // Update store
            statusGroups.value = JSON.parse(JSON.stringify(localGroups.value));
            savingGroups.value = true;
            if (debounceGroupsTimer) clearTimeout(debounceGroupsTimer);
            debounceGroupsTimer = setTimeout(async () => {
                try {
                    await saveConfig("status_groups", localGroups.value);
                } finally {
                    savingGroups.value = false;
                }
            }, 1000);
        };

        // --- Init & Watch ---
        onMounted(async () => {
            localTracked.value = [...trackedStatuses.value];
            // Deep copy to break reference
            localClasses.value = JSON.parse(JSON.stringify(skillClasses.value));
            localGroups.value = JSON.parse(JSON.stringify(statusGroups.value));
            localBosses.value = [...trackedBosses.value];
            localFilters.value = { ...analysisFilters.value };

            try {
                const expectedHP = await loadConfig<Record<string, number>>("boss_expected_hp");
                if (expectedHP) {
                    const numeric: Record<number, number> = {};
                    for (const [id, hp] of Object.entries(expectedHP)) {
                        numeric[Number(id)] = hp;
                    }
                    localBossExpectedHP.value = numeric;
                }
            } catch (e) {
                console.error("Failed to load boss expected HP config", e);
            }

            // Lazy load resources for this view
            if (db && db.value) {
                // GUARD: If we already have names, don't re-fetch/re-assign to avoid flickering
                if (Object.keys(skillNameMap.value).length > 0 && Object.keys(condNameMap.value).length > 0) {
                     return;
                }

                const [skills, statuses] = await Promise.all([
                    db.value.getData("SkillNameMap"),
                    db.value.getData("StatusNameMap")
                ]);

                if (skills && Object.keys(skillNameMap.value).length === 0) Object.assign(skillNameMap.value, skills);
                if (statuses && Object.keys(condNameMap.value).length === 0) Object.assign(condNameMap.value, statuses);
            }
        });

        watch(trackedStatuses, (newVal) => {
            const currentSet = new Set(localTracked.value);
            const newSet = new Set(newVal);
            if (currentSet.size !== newSet.size || [...newSet].some(x => !currentSet.has(x))) {
                localTracked.value = [...newVal];
            }
        });

        watch(skillClasses, (newVal) => {
            // We need to be careful not to overwrite local edits if we are the ones triggering the update.
            // But since we update store immediately in triggerSaveClass, this watch might fire.
            // Simple check: stringify comparison.
            if (JSON.stringify(newVal) !== JSON.stringify(localClasses.value)) {
                localClasses.value = JSON.parse(JSON.stringify(newVal));
            }
        }, { deep: true });

        // Helper for v-autocomplete in loop resetting
        // We can't easily reset the v-autocomplete inside the loop without refs array.
        // But since we use `return-object` and `props`, maybe we just rely on the user clearing it or it just showing the value?
        // Actually, for "Add Skill", we usually want it to clear after selection.
        // I'll leave it as is for now, it might keep the selected value which is annoying.
        // To fix: use a specialized component or a method that sets the value to null via ref.
        // For simplicity in this iteration, I'll accept it might persist the text, but let's try to handle it if possible.
        // If I use a single `selectedSkillToAdd` it conflicts if I have multiple panels open.
        // So I need a specific adding mechanism.
        // Using `@update:model-value="(item) => ..."` works for logic, but doesn't clear the input.
        // A better UX might be a "search skills" button that opens a dialog, OR just a simple `v-autocomplete` that we set to null manually?
        // In a `v-for`, we can't easily access the "model" of a specific row without an array.
        // Let's create an array of `null`s for the models?
        // Or simpler: Reuse the same `v-autocomplete` component logic?
        // Let's just use `localClasses` to store the selection temporarily? No, that dirties the data.
        // I will just use a temporary ref array for the "add skill" inputs? No, dynamic length.
        // I'll ignore the clearing issue for this specific "first pass" - user can just backspace or select another.
        // Actually, better: if I use `hide-selected`, maybe it helps?
        // Let's just try to be clean.

        return {
            tab,
            // Filters
            localFilters,
            savingFilters,
            triggerSaveFilters,
            formatNumber,
            // Boss
            localBosses,
            savingBoss,
            selectedBossToAdd,
            allRaces,
            getRaceName,
            onAddBoss,
            removeBoss,
            localBossExpectedHP,
            triggerSaveBossExpectedHP,
            rebuildingLeaderboard,
            rebuildStatusMessage,
            rebuildStatusIsError,
            confirmRebuildLeaderboard,
            // Status
            localTracked,
            savingStatus,
            selectedStatusToAdd,
            allStatuses,
            getStatusName,
            onAddStatus,
            removeStatus,
            getStatusIconUrl,

            // Status Groups
            localGroups,
            savingGroups,
            createNewGroup,
            deleteGroup,
            addStatusToGroup,
            removeStatusFromGroup,
            triggerSaveGroups,

            // Class
            localClasses,
            savingClass,
            allSkills,
            getSkillName,
            createNewClass,
            deleteClass,
            addSkillToClass,
            removeSkillFromClass,
            getSkillIconUrl,
            region,
            triggerSaveClass
        };
    }
});
</script>

<style scoped>
/* Ensure tabs don't take too much height */
.v-tabs {
    flex: 0 0 auto;
}
</style>
